// dependencies.
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth'
// components.
import AuthTemplate from '../../templates/AuthScreen'
import { Loader } from '../../components/Icon'
// utils.
import { auth } from '../../js/firebase/firebase'
import notifyToast from '../../js/notifyToast'

const ResetActionScreen = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [values, setValues] = useState({})
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isPassword2Visible, setIsPassword2Visible] = useState(false)
  const [isValidating, setIsValidating] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [linkError, setLinkError] = useState('')
  const [formErrors, setFormErrors] = useState({})

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const mode = queryParams.get('mode') || ''
  const oobCode = queryParams.get('oobCode') || ''

  useEffect(() => {
    const validateCode = async () => {
      if (mode !== 'resetPassword') {
        setLinkError('El enlace no corresponde a un restablecimiento de contraseña.')
        setIsValidating(false)
        return
      }

      if (!oobCode) {
        setLinkError('El enlace no es válido o está incompleto.')
        setIsValidating(false)
        return
      }

      try {
        await verifyPasswordResetCode(auth, oobCode)
      } catch (error) {
        setLinkError('El enlace expiró o ya fue utilizado. Solicite uno nuevo.')
      } finally {
        setIsValidating(false)
      }
    }

    validateCode()
  }, [mode, oobCode])

  const handleValueChange = (newValues) => {
    setValues(newValues)
    if (Object.keys(formErrors).length > 0) setFormErrors({})
  }

  const handleSubmit = async () => {
    if (isSubmitting || isValidating || linkError) return
    const password = values.password || ''
    const password2 = values.password2 || ''
    const errors = {}

    if (!password) errors.password = 'Debe ingresar una contraseña.'
    if (!password2) errors.password2 = 'Debe repetir la contraseña.'
    if (password && password2 && password !== password2) {
      errors.password2 = 'Las contraseñas no coinciden.'
    }
    if (password && password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres.'
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    try {
      setIsSubmitting(true)
      await confirmPasswordReset(auth, oobCode, password)
      notifyToast('La contraseña se actualizó correctamente.', 'success')
      navigate('/signin')
    } catch (error) {
      notifyToast('No fue posible actualizar la contraseña. Solicite un nuevo enlace.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const description =
    linkError || 'Ingrese su nueva contraseña para completar el restablecimiento de acceso.'

  const resetActionForm = [
    {
      iconL: 'edit',
      iconR: 'view',
      iconRColor: isPasswordVisible ? 'black' : 'ash',
      handleIconRClick: () => setIsPasswordVisible((prev) => !prev),
      name: 'password',
      type: isPasswordVisible ? 'text' : 'password',
      placeholder: 'Nueva contraseña',
      required: true,
    },
    {
      iconL: 'edit',
      iconR: 'view',
      iconRColor: isPassword2Visible ? 'black' : 'ash',
      handleIconRClick: () => setIsPassword2Visible((prev) => !prev),
      name: 'password2',
      type: isPassword2Visible ? 'text' : 'password',
      placeholder: 'Repita la contraseña',
      required: true,
    },
  ]

  return (
    <AuthTemplate
      disbledSubmit={isSubmitting || isValidating || !!linkError}
      description={description}
      form={resetActionForm}
      formErrors={formErrors}
      submitLabel="Actualizar contraseña"
      topbarIcon="caret-left"
      topbarTitle="Cambiar contraseña"
      onChange={handleValueChange}
      onSubmit={handleSubmit}
      onTopbarAction={() => navigate('/signin')}
    >
      {(isSubmitting || isValidating) && <Loader />}
    </AuthTemplate>
  )
}

export default ResetActionScreen
